import { EmployeePaymentMethodStatus, EnrollmentStatus } from '@zorro/clients';
import { APP_ROUTES, getFullName } from '@zorro/common-utils';
import { Button } from '@zorro/design-system/v2/Button';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { Tabs } from '@zorro/design-system/v2/Tabs';
import { Group, Stack } from '@zorro/web/design-system';
import { useAppRouter, useMonolithQuery, useRoles } from '@zorro/web/ui-utils';
import { ReactElement, useState } from 'react';
import { $enum } from 'ts-enum-util';

import { useAnalytics } from '../Analytics';
import {
  CommentsButton,
  CommentsDrawer,
  useEnrollmentComments,
} from '../Comments';
import { ReportLifeEventForm } from '../EmployeeActions/EnrollmentActions/ReportLifeEventForm';
import { paymentsTabEnabledEnrollmentStatuses } from '../EnrollmentStatus';

export enum EmployeeLayoutTabType {
  ENROLLMENTS = 'ENROLLMENTS',
  PAYMENTS = 'PAYMENTS',
}

const reportLifeEventEnabledEnrollmentStatuses = new Set([
  EnrollmentStatus.ELECTION_SUBMITTED,
  EnrollmentStatus.WAIVED_ELECTION,
  EnrollmentStatus.ENROLLMENT_IN_PROCESS,
  EnrollmentStatus.ENROLLMENT_CONFIRMED,
  EnrollmentStatus.ACTIVE_COVERAGE,
  EnrollmentStatus.WAIVED_COVERAGE,
]);

const getPaymentTabEnabled = (
  enrollmentStatus: EnrollmentStatus,
  isEmployerAdmin?: boolean,
  isPaymentMethodValid?: boolean
) => {
  const enabledStatuses = new Set(paymentsTabEnabledEnrollmentStatuses);

  if (!isEmployerAdmin || (isEmployerAdmin && isPaymentMethodValid)) {
    enabledStatuses.add(EnrollmentStatus.ELECTION_SUBMITTED);
  }

  return enabledStatuses.has(enrollmentStatus);
};

const allEmployeeLayoutTabs = Object.values(EmployeeLayoutTabType);
const getEmployeeLayoutTabConfig = (
  tab: EmployeeLayoutTabType,
  enrollmentStatus: EnrollmentStatus,
  isEmployerAdmin?: boolean,
  isPaymentMethodValid?: boolean
) => {
  return $enum.mapValue(tab).with<{
    label: string;
    route: keyof typeof APP_ROUTES;
    isDisabled: boolean;
  }>({
    [EmployeeLayoutTabType.ENROLLMENTS]: {
      label: 'Enrollment',
      route: isEmployerAdmin
        ? 'DON_EMPLOYEE_PLANS_TAB'
        : 'OPS_EMPLOYEE_PLANS_TAB',
      isDisabled: false,
    },
    [EmployeeLayoutTabType.PAYMENTS]: {
      label: 'Payments',
      route: isEmployerAdmin
        ? 'DON_EMPLOYEE_PAYMENT_TAB'
        : 'OPS_EMPLOYEE_PAYMENT_TAB',
      isDisabled: !getPaymentTabEnabled(
        enrollmentStatus,
        isEmployerAdmin,
        isPaymentMethodValid
      ),
    },
  });
};

type Props = {
  tab: EmployeeLayoutTabType;
  children: ReactElement;
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  shouldShowReportLifeEventButton?: boolean;
  isAdminDashboard?: boolean;
  isDon?: boolean;
};

export const EmployeeLayoutTab = ({
  tab,
  employerId,
  employeeId,
  onboardingPeriodId,
  children,
  shouldShowReportLifeEventButton = false,
  isAdminDashboard,
  isDon,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { navigateToRoute } = useAppRouter();
  const { sendEvent } = useAnalytics();

  const { isManagement } = useRoles();

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });
  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });

  const { data: comments = [] } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindManyEnrollmentComments',
    params: [onboardingPeriodId],
    enabled: isManagement,
  });

  const { isFetching, data: paymentMethod } = useMonolithQuery({
    method: 'paymentsControllerGetPaymentMethod',
    params: [employeeId, onboardingPeriodId],
  });

  const { showComments, setShowComments, selectedCommentId } =
    useEnrollmentComments();

  if (!onboardingPeriod || !employee) {
    return null;
  }

  const { enrollmentStatus } = onboardingPeriod;

  const isPaymentMethodValid =
    paymentMethod?.status === EmployeePaymentMethodStatus.VALID;

  const handleReportLifeEventClick = () => {
    void sendEvent('click_report_life_event', {
      onboarding_period_id: onboardingPeriodId,
      page: isAdminDashboard
        ? 'enrollment_details-admin'
        : 'enrollment_details',
    });
    setIsModalOpen(true);
  };

  return (
    <>
      <Stack mb="6rem" gap="lg">
        <Group justify="space-between" maw={900}>
          <Tabs mt="sm" keepMounted={false} value={tab}>
            <Tabs.List>
              {allEmployeeLayoutTabs.map((newTab) => {
                const { label, route, isDisabled } = getEmployeeLayoutTabConfig(
                  newTab,
                  enrollmentStatus,
                  isDon,
                  isPaymentMethodValid
                );

                return (
                  <Tabs.Tab
                    onClick={() => {
                      void sendEvent('click_enrollment_tab', {
                        onboardingPeriod_ID: onboardingPeriodId,
                        tab: newTab,
                      });

                      navigateToRoute(route, [
                        { employerId, employeeId, onboardingPeriodId },
                      ]);
                    }}
                    disabled={isDisabled || isFetching}
                    value={newTab}
                    key={newTab}
                  >
                    {label}
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>
          </Tabs>
          {shouldShowReportLifeEventButton &&
            reportLifeEventEnabledEnrollmentStatuses.has(enrollmentStatus) && (
              <Button
                size="md"
                variant="secondary"
                onClick={handleReportLifeEventClick}
              >
                Report life event
              </Button>
            )}
          {isManagement && (
            <CommentsButton
              commentsCount={comments.length}
              onClick={() => setShowComments(true)}
            />
          )}
        </Group>
        {children}
      </Stack>

      {isManagement && (
        <CommentsDrawer
          shouldShowComments={showComments}
          onboardingPeriodId={onboardingPeriodId}
          isSpecialEnrollment={onboardingPeriod.isSpecialEnrollment}
          enrollmentStartDate={onboardingPeriod.enrollmentEffectiveFrom}
          employeeId={employeeId}
          fullName={getFullName(employee)}
          employerId={employerId}
          commentsCount={comments.length}
          selectedCommentId={selectedCommentId}
          setShowComments={setShowComments}
        />
      )}
      <Drawer
        opened={isModalOpen}
        title="Report a Qualifying Life Event"
        onClose={() => setIsModalOpen(false)}
      >
        <ReportLifeEventForm
          employee={employee}
          onboardingPeriod={onboardingPeriod}
          onSuccess={(onboardingPeriodId) => {
            setIsModalOpen(false);
            if (isDon) {
              if (onboardingPeriodId) {
                navigateToRoute('DON_EMPLOYEE_PLANS_TAB', [
                  {
                    employerId,
                    employeeId,
                    onboardingPeriodId,
                  },
                ]);
              } else {
                navigateToRoute('DON_EMPLOYEE_PAGE', [
                  { employerId, employeeId },
                ]);
              }
              return;
            }

            if (onboardingPeriodId) {
              navigateToRoute('OPS_EMPLOYEE_PLANS_TAB', [
                {
                  employerId,
                  employeeId,
                  onboardingPeriodId,
                },
              ]);
            } else {
              navigateToRoute('OPS_EMPLOYEE_PAGE', [
                { employerId, employeeId },
              ]);
            }
          }}
        />
      </Drawer>
    </>
  );
};
